import React, { useState, useEffect } from 'react';
import { FiActivity, FiClock, FiTrendingUp, FiZap } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { parentAnalyticsAPI } from '../../../api/parentEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Section,
  SectionTitle,
  Grid,
  Card,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Badge,
  CircularProgress,
  CircularProgressValue,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from '../Analytics.styles';

const LearningIntelligence = ({ studentId, days = 30 }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [studentId, days]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await parentAnalyticsAPI.getLearningIntelligence(studentId, { days });
      setData(response.data.learning_intelligence);
    } catch (error) {
      showToast('error', 'Failed to load learning intelligence data');
      console.error('Error fetching learning intelligence:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEngagementColor = (score) => {
    if (score >= 75) return theme.colors.success;
    if (score >= 50) return theme.colors.warning;
    return theme.colors.error;
  };

  const getRatingStars = (rating) => {
    return '⭐'.repeat(Math.min(rating || 0, 5));
  };

  const prepareFocusIndexData = () => {
    if (!data || !data.focus_index) return [];
    return Object.entries(data.focus_index)
      .map(([hour, stats]) => ({
        hour: `${hour}:00`,
        score: stats.average_score,
        attempts: stats.attempts,
      }))
      .sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
  };

  const preparePerformanceRadarData = () => {
    if (!data) return [];
    return [
      { metric: 'Engagement', value: data.engagement_score, fullMark: 100 },
      { metric: 'Consistency', value: data.consistency_score, fullMark: 100 },
      { metric: 'Completion', value: data.completion_rate, fullMark: 100 },
      { metric: 'Comprehension', value: data.comprehension_rate, fullMark: 100 },
    ];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: theme.shadows.lg,
        }}>
          <p style={{ margin: 0, fontWeight: 600, color: theme.colors.textPrimary }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0 0 0', color: entry.color, fontSize: '14px' }}>
              {entry.name}: {entry.value.toFixed(1)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading intelligence data..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiActivity size={64} />
        <h3>No Data Available</h3>
        <p>Intelligence data will appear once the student starts learning</p>
      </EmptyState>
    );
  }

  return (
    <>
      {/* Core Metrics */}
      <Section>
        <SectionTitle>
          <FiActivity />
          Core Intelligence Metrics
        </SectionTitle>
        <Grid minWidth="280px">
          <StatCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <StatLabel>Engagement Score</StatLabel>
              <Badge color={getEngagementColor(data.engagement_score)}>
                Level {data.engagement_rating}
              </Badge>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px' }}>
              <StatValue color={getEngagementColor(data.engagement_score)}>
                {data.engagement_score.toFixed(1)}%
              </StatValue>
              <CircularProgress
                percentage={data.engagement_score}
                color={getEngagementColor(data.engagement_score)}
                size="100px"
              >
                <CircularProgressValue>
                  {data.engagement_score.toFixed(0)}
                </CircularProgressValue>
              </CircularProgress>
            </div>
            <StatDescription>
              Rating: {getRatingStars(data.engagement_rating)}
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Learning Velocity</StatLabel>
            <StatValue color={theme.colors.primary}>
              {data.learning_velocity.toFixed(3)}
            </StatValue>
            <StatDescription>
              📚 {data.learning_velocity_description}
            </StatDescription>
            <div style={{ marginTop: '12px', fontSize: '14px', color: theme.colors.textSecondary }}>
              Courses completed per day
            </div>
          </StatCard>

          <StatCard>
            <StatLabel>Consistency Score</StatLabel>
            <StatValue color={theme.colors.secondary}>
              {data.consistency_score.toFixed(1)}%
            </StatValue>
            <StatDescription>
              Study pattern regularity
            </StatDescription>
            <div style={{
              marginTop: '12px',
              padding: '8px',
              background: theme.colors.backgroundTertiary,
              borderRadius: '8px',
              fontSize: '14px',
              textAlign: 'center',
            }}>
              {data.consistency_score >= 70 ? '✅ Excellent' : data.consistency_score >= 50 ? '🔶 Good' : '⚠️ Needs Improvement'}
            </div>
          </StatCard>

          <StatCard>
            <StatLabel>Completion Rate</StatLabel>
            <StatValue color={theme.colors.info}>
              {data.completion_rate.toFixed(1)}%
            </StatValue>
            <StatDescription>
              Content completion success
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Comprehension & Performance */}
      <Section>
        <SectionTitle>
          <FiZap />
          Comprehension & Peak Performance
        </SectionTitle>
        <Grid minWidth="300px">
          <StatCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <StatLabel>Comprehension Rate</StatLabel>
              <Badge color={theme.colors.success}>
                Level {data.comprehension_rating}
              </Badge>
            </div>
            <StatValue color={theme.colors.success}>
              {data.comprehension_rate.toFixed(1)}%
            </StatValue>
            <StatDescription>
              First-attempt quiz pass rate<br />
              {getRatingStars(data.comprehension_rating)}
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Peak Performance Hour</StatLabel>
            <StatValue style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '48px' }}>🕐</span>
              {data.peak_performance_hour}:00
            </StatValue>
            <StatDescription>
              Best performance: {data.peak_performance_score.toFixed(1)}%
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Learning Stamina */}
      <Section>
        <SectionTitle>
          <FiClock />
          Learning Stamina & Activity
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Study Streak</StatLabel>
            <StatValue style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '48px' }}>🔥</span>
              {data.learning_stamina.study_streak} days
            </StatValue>
            <StatDescription>
              Keep it up! 
              {data.learning_stamina.study_streak >= 7 && ' 🎉 Amazing!'}
              {data.learning_stamina.study_streak >= 30 && ' 🏆 Outstanding!'}
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Total Study Time</StatLabel>
            <StatValue color={theme.colors.accent}>
              {data.learning_stamina.total_hours.toFixed(1)}h
            </StatValue>
            <StatDescription>
              in the last {data.period_days} days
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Daily Average</StatLabel>
            <StatValue color={theme.colors.info}>
              {data.learning_stamina.avg_daily_minutes.toFixed(0)} min
            </StatValue>
            <StatDescription>
              per active day
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Active Days</StatLabel>
            <StatValue color={theme.colors.secondary}>
              {data.learning_stamina.active_days}
            </StatValue>
            <StatDescription>
              out of {data.period_days} days<br />
              ({((data.learning_stamina.active_days / data.period_days) * 100).toFixed(0)}% activity rate)
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Performance Radar Chart */}
      <Section>
        <SectionTitle>
          <FiTrendingUp />
          Performance Overview
        </SectionTitle>
        <Card>
          <ChartContainer height="400px">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={preparePerformanceRadarData()}>
                <PolarGrid stroke={theme.colors.border} />
                <PolarAngleAxis
                  dataKey="metric"
                  tick={{ fill: theme.colors.textSecondary, fontSize: 14 }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={{ fill: theme.colors.textSecondary }}
                />
                <Radar
                  name="Performance"
                  dataKey="value"
                  stroke={theme.colors.primary}
                  fill={theme.colors.primary}
                  fillOpacity={0.6}
                />
                <Tooltip content={<CustomTooltip />} />
              </RadarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>
      </Section>

      {/* Focus Index - Performance by Hour */}
      {Object.keys(data.focus_index || {}).length > 0 && (
        <Section>
          <SectionTitle>
            Focus Index - Performance by Hour
          </SectionTitle>
          <Card>
            <p style={{ color: theme.colors.textSecondary, marginBottom: '20px' }}>
              Shows average quiz performance at different hours of the day
            </p>
            <ChartContainer height="400px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={prepareFocusIndexData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis
                    dataKey="hour"
                    tick={{ fill: theme.colors.textSecondary }}
                  />
                  <YAxis
                    tick={{ fill: theme.colors.textSecondary }}
                    domain={[0, 100]}
                    label={{ value: 'Average Score (%)', angle: -90, position: 'insideLeft', fill: theme.colors.textSecondary }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="score"
                    fill={theme.colors.primary}
                    name="Score"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
            <div style={{ marginTop: '16px', padding: '12px', background: theme.colors.backgroundSecondary, borderRadius: '8px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: theme.colors.textSecondary }}>
                💡 <strong>Insight:</strong> Schedule important learning sessions during peak performance hours for better results.
              </p>
            </div>
          </Card>
        </Section>
      )}
    </>
  );
};

export default LearningIntelligence;

